import axios from 'axios';
import toastr from 'toastr';
import store from "@frontend/store";
import {MUTATIONS} from "@frontend/store/file-browser-product-selector";
import {getObjectsForOrder} from "@frontend/group/modules/copy-and-resize";
import {IMAGE_PREFIX_ID} from "@frontend/constants/templates-field-prefixes";
import {FIELD_TYPE_BACKGROND_THEME_IMAGE} from "@frontend/constants/type-fields-of-template";
import {DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES} from "@frontend/group/modules/additional-fields";

export function validateProductImage(file, maxSizeMB = 10) {
  const MAX_FILE_SIZE = maxSizeMB * 1024 * 1024;

  if (!file) {
    toastr.error('No file selected');
    return false;
  }

  if (!file.type.startsWith('image/')) {
    toastr.error('Please upload an image file');
    return false;
  }

  if (file.size > MAX_FILE_SIZE) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
    toastr.error(`File size (${sizeMB}MB) exceeds the maximum allowed size of ${maxSizeMB}MB`);
    return false;
  }

  return true;
}

export async function uploadProductImage(file) {
  const formData = new FormData();
  formData.append('upload_images[]', file);
  formData.append('image_type', 'product_image');
  formData.append('max_size', '10000');
  formData.append('resize', '99');
  formData.append('dpi', '75');
  formData.append('company_id', '-1');

  toastr.info('Uploading product image...');
  
  const response = await axios.post('/file/uploadimg/store', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });

  return response.data;
}

export async function fetchProductImagesData(fileIds, layoutId) {
  const response = await axios.post('/banner/view', {
    file_ids: fileIds,
    show_warning: true
  });

  return response.data;
}

export function mapResponseToItem(imageData) {
  let fileId = '';
  if (imageData.name.startsWith('http://') || imageData.name.startsWith('https://')) {
    fileId = imageData.name;
  } else {
    fileId = imageData.name.substring(0, imageData.name.lastIndexOf('.')) || imageData.name;
  }

  return {
    id: imageData.id,
    name: imageData.name,
    product_name: imageData.product_name || null,
    brand: imageData.brand || null,
    thumbnail: imageData.thumbnail,
    path: imageData.path,
    company_id: imageData.company_id,
    status: 'indexed',
    last_updated: imageData.updated_at ? imageData.updated_at.split('T')[0] : new Date().toISOString().split('T')[0],
    gtin: imageData.child_gtin || imageData.parent_gtin || null,
    graphic_version_number: null,
    image_start_date: null,
    image_end_date: null,
    sub_brand: null,
    target_market: null,
    file_name: null,
    sector: null,
    url: imageData.url || `${window.location.origin}/share?file=${imageData.thumbnail}`,
    full_url: imageData.url || `${window.location.origin}/share?file=${imageData.path}`,
    type: 'image',
    count: 1,
    fileId: fileId,
    width: imageData.width,
    height: imageData.height,
    depth: imageData.depth,
    ASIN: imageData.ASIN || null,
    UPC: imageData.UPC || null,
  };
}

export async function getAllProductImages(existingImages, newFileId, layoutId) {
  const items = [];

  // Fetch existing images
  if (existingImages && existingImages.length > 0) {
    const existingFileIds = existingImages.map(img => img.fileName).join(',');

    try {
      const existingResponse = await fetchProductImagesData(existingFileIds, layoutId);

      if (existingResponse.status === 'success' && existingResponse.files) {
        for (const fileData of existingResponse.files) {
          const imageData = fileData.related_files?.[0] || fileData;
          items.push(mapResponseToItem(imageData));
        }
      }
    } catch (error) {
      console.error('Error fetching existing product images:', error);
      toastr.warning('Could not load existing product images');
    }
  }

  // Fetch newly uploaded image
  if (newFileId) {
    try {
      const newImageResponse = await fetchProductImagesData(newFileId, layoutId);

      if (newImageResponse.status === 'success' && newImageResponse.files?.[0]) {
        const fileData = newImageResponse.files[0];
        const imageData = fileData.related_files?.[0] || fileData;
        items.push(mapResponseToItem(imageData));
      }
    } catch (error) {
      console.error('Error fetching new image data:', error);
      throw error;
    }
  }

  return items;
}

export async function handleProductImageUpload(file, layoutId, existingImages = []) {
  try {
    if (!validateProductImage(file)) {
      return null;
    }

    const uploadResponse = await uploadProductImage(file);

    // Extract file info
    const filename = uploadResponse.filenames[0];
    const fileId = filename.substring(0, filename.lastIndexOf('.')) || filename;

    // Get all images (existing + new)
    const allImages = await getAllProductImages(existingImages, fileId, layoutId);

    toastr.success('Product image uploaded successfully!');

    return {
      items: allImages,
      newFileId: fileId,
      filename: filename
    };

  } catch (error) {
    console.error('Error uploading product image:', error);
    toastr.error('Failed to upload product image: ' + (error.response?.data?.message || error.message));
    return null;
  }
}

export function getProductImageFields(preview) {
  let objects = getObjectsForOrder(preview.canvas.getObjects());
  const usersFieldsOrder = preview.data?.users_fields_order || [];

  objects = [...objects].sort((a, b) => {
    const indexA = usersFieldsOrder.indexOf(a.templateFieldId);
    const indexB = usersFieldsOrder.indexOf(b.templateFieldId);

    // Objects not found in usersFieldsOrder go to the end
    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;

    return indexA - indexB;
  });

  return objects.map(object => {
    if (!object || !object.target) return null;
    
    if (object.target._element && object.id.startsWith(IMAGE_PREFIX_ID)) {
      const url = new URL(object.target._element.currentSrc);
      if (url.searchParams.size > 0) {
        url.searchParams.set('lightweight', '');
        url.searchParams.set('scale_width', '40');
      }
      return {
        id: object.target.id || '',
        name: object.name || '',
        type: object.target.templateField?.type || FIELD_TYPE_BACKGROND_THEME_IMAGE,
        thumbnail: url || DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES.additional_background_images,
        visible: object.visible ?? null,
        templateFieldId: object.templateFieldId,
        fileName: object.fileName
      }
    }
    
    return null;
  }).filter(field => field?.visible);
}

export async function uploadProductImageAndDraw({file, canvas, selectedLayoutId, existingImages = [], recentImage = null}) {
  let result = recentImage;
  if (!recentImage) {
    result = await handleProductImageUpload(file, selectedLayoutId, existingImages);
  }
  
  if (!result) return;

  await store.dispatch('fileBrowserProductSelector/clearSelection');
  await store.commit(`fileBrowserProductSelector/${MUTATIONS.SET_PICKED_ITEMS}`, []);

  const items = result.items;
  await store.commit(`fileBrowserProductSelector/${MUTATIONS.SET_ITEMS}`, items);

  items.forEach(item => {
    if (!store.getters['fileBrowserProductSelector/isItemPicked'](item)) {
      store.dispatch('fileBrowserProductSelector/togglePickedItem', item);
      store.commit(`fileBrowserProductSelector/${MUTATIONS.TOGGLE_SELECTED_ITEMS}`, item);
    }
  });

  canvas.fire('drag-and-drop:save-images', selectedLayoutId);
}
